import CloseIcon from '@mui/icons-material/Close';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import FmdGoodIcon from '@mui/icons-material/FmdGood';
import { IconButton, Paper, Stack, Tooltip } from '@mui/material';
import { FullscreenControl, GeolocateControl, NavigationControl } from 'react-map-gl/mapbox';

import type { GeolocateControl as MapboxGeolocateControl } from 'mapbox-gl';

interface InstanceMapControlsProps {
    forceShowGeolocate?: boolean;
    geolocateRef?: React.Ref<MapboxGeolocateControl>;
    hasInstanceId: boolean;
    isFullscreen?: boolean;
    moreThanOneInstance?: boolean;
    onClose?: () => void;
    onFitAllInstances?: () => void;
    onlyCurrentEffective: boolean;
    onToggleShowOnlyCurrent: () => void;
}

export default function InstanceMapControls({
    forceShowGeolocate = false,
    geolocateRef,
    hasInstanceId,
    isFullscreen = false,
    moreThanOneInstance,
    onClose,
    onFitAllInstances,
    onlyCurrentEffective,
    onToggleShowOnlyCurrent,
}: Readonly<InstanceMapControlsProps>) {
    return (
        <>
            {!hasInstanceId || isFullscreen || forceShowGeolocate ? (
                <GeolocateControl
                    fitBoundsOptions={{ maxZoom: 15 }}
                    position="top-left"
                    positionOptions={{ enableHighAccuracy: true, maximumAge: 30_000, timeout: 6000 }}
                    ref={geolocateRef}
                    showUserLocation
                    trackUserLocation
                />
            ) : null}
            <FullscreenControl position="top-left" />
            <NavigationControl position="top-left" />
            {(moreThanOneInstance && hasInstanceId) || (!hasInstanceId && onFitAllInstances) ? (
                <Paper
                    elevation={2}
                    sx={{
                        p: 0.5,
                        position: 'absolute',
                        right: 8,
                        top: hasInstanceId && !isFullscreen ? 10 : 54,
                        zIndex: 1,
                    }}
                >
                    <Stack sx={{ display: 'flex' }}>
                        {moreThanOneInstance && hasInstanceId ? (
                            <Tooltip
                                placement="left"
                                title={onlyCurrentEffective ? 'Show all instances' : 'Only current instance'}
                            >
                                <IconButton
                                    aria-label={onlyCurrentEffective ? 'Show all instances' : 'Only current instance'}
                                    color="primary"
                                    onClick={onToggleShowOnlyCurrent}
                                    size="small"
                                >
                                    {onlyCurrentEffective ? (
                                        <FitScreenIcon fontSize="small" />
                                    ) : (
                                        <FmdGoodIcon fontSize="small" />
                                    )}
                                </IconButton>
                            </Tooltip>
                        ) : null}
                        {!hasInstanceId && onFitAllInstances ? (
                            <Tooltip placement="left" title="Fit all instances">
                                <IconButton
                                    aria-label="Fit all instances"
                                    color="primary"
                                    onClick={onFitAllInstances}
                                    size="small"
                                >
                                    <FitScreenIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                    </Stack>
                </Paper>
            ) : null}
            {isFullscreen ? (
                <Paper elevation={2} sx={{ borderRadius: '50%', position: 'absolute', right: 12, top: 10, zIndex: 1 }}>
                    <Tooltip title="Close map">
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Paper>
            ) : null}
        </>
    );
}
